import type { Stack0HandlerOptions, UploadHandler } from "./types";

/**
 * Creates an upload handler that calls your API endpoint
 *
 * Your server endpoint should use the Stack0 SDK to create presigned URLs.
 * The client uploads directly to S3 using the presigned URL.
 *
 * @example
 * ```tsx
 * // Client-side usage
 * import { FileUpload, createStack0Handler } from "@stack0/elements";
 *
 * const handler = createStack0Handler({
 *   endpoint: "/api/upload",
 * });
 *
 * <FileUpload handler={handler} />
 * ```
 *
 * @example
 * ```ts
 * // Server-side API route (Next.js App Router)
 * // app/api/upload/route.ts
 * import { stack0 } from "@stack0/sdk";
 *
 * export async function POST(request: Request) {
 *   const { filename, mimeType, size, folder } = await request.json();
 *
 *   const result = await stack0.cdn.createUpload({
 *     filename,
 *     mimeType,
 *     size,
 *     folder,
 *     public: true, // Creates a public presigned URL
 *   });
 *
 *   return Response.json({
 *     uploadUrl: result.uploadUrl,
 *     assetId: result.assetId,
 *   });
 * }
 * ```
 */
export function createStack0Handler(
  options: Stack0HandlerOptions
): UploadHandler {
  const { endpoint, confirmEndpoint, headers = {}, folder } = options;

  return {
    getUploadUrl: async (file: File) => {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
          folder: folder || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to get upload URL");
      }

      const data = await response.json();
      return {
        uploadUrl: data.uploadUrl,
        assetId: data.assetId,
      };
    },

    onUploadComplete: async (assetId: string) => {
      const confirmUrl = confirmEndpoint || `${endpoint}/confirm`;

      const response = await fetch(confirmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ assetId }),
      });

      if (!response.ok) {
        // Non-critical - upload succeeded, just couldn't confirm
        console.warn("[Stack0] Could not confirm upload:", assetId);
        return {};
      }

      const data = await response.json();
      return {
        url: data.url,
      };
    },

    onError: (error: Error, file: File) => {
      console.error(`[Stack0] Upload failed for ${file.name}:`, error.message);
    },
  };
}
